import { Dispatch, SetStateAction } from 'react'
import { TFunction } from 'react-i18next'
import api from 'api'
import { ITEMS_PER_PAGE } from 'constant'
import { NotifierType } from 'components/Notifier'
import { Agency } from 'types/agency'
import { Disposition, DispositionCorrection } from 'types/disposition'
import { UpsertCorrectionVariables } from 'api/dispositions/types'
import DownloadUtils from 'utils/download'
import VariableUtils from 'utils/variable'

export type AddNotification = (type: NotifierType, text: string | string[], time?: number) => void

/**
 * The backend does not expose the stored file names and the Shrine urls point at opaque S3
 * keys, so the download name is rebuilt here. Global ids are base64 and may contain
 * characters that are not filename safe.
 */
const fileNameFor = (prefix: string, id: string, extension: string): string =>
  `${prefix}_${id.replace(/[^a-zA-Z0-9]/g, '')}.${extension}`

export default class AgencyDispositionsService {
  static getDispositions = (
    agencyId: Agency['id'],
    pageNumber: number,
    setDispositions: Dispatch<SetStateAction<Disposition[]>>,
    setTotalCount: Dispatch<SetStateAction<number>>,
    setLoading: Dispatch<SetStateAction<boolean>>
  ): Promise<void> => {
    setLoading(true)

    return api.dispositions
      .getDispositions({
        agencyId,
        first: pageNumber * ITEMS_PER_PAGE,
        last: ITEMS_PER_PAGE,
      })
      .then(res => {
        const { dispositions } = res.data

        setDispositions(dispositions.nodes)
        setTotalCount(dispositions.totalCount)
      })
      .catch(() => void {})
      .finally(() => setLoading(false))
  }

  /**
   * Mirrors `DispositionCorrection#compute_prices` - a positive percent change raises the price,
   * a negative one lowers it.
   */
  static correctedPrice = (price: number, percentChange: number): number =>
    price * (1 + percentChange / 100)

  /**
   * Corrections compound - the backend counts every one of them from the price left by the
   * previous correction (`DispositionCorrection#base_price`) and falls back to the disposition
   * price only for the first one. Corrections come sorted by `createdAt` descending, so the
   * previous one is the first entry other than the correction being edited.
   */
  static basePrice = (
    disposition: Disposition,
    correction?: DispositionCorrection | null
  ): number => {
    const previous = disposition.corrections.find(el => el.id !== correction?.id)

    return previous ? previous.correctedPrice : disposition.price
  }

  /** Only one unsent correction per disposition is allowed by the backend. */
  static hasUnsentCorrection = (disposition: Disposition): boolean =>
    disposition.corrections.some(correction => !correction.sentAt)

  static formatPrice = (value: number): string =>
    VariableUtils.formatNumber(value, { style: 'currency', currency: 'PLN' })

  /** The sign carries the direction of the correction, so it is always spelled out. */
  static formatPercent = (percentChange: number): string =>
    `${percentChange > 0 ? '+' : ''}${VariableUtils.formatNumber(percentChange)}%`

  static unsentCorrections = (disposition: Disposition): DispositionCorrection[] =>
    disposition.corrections.filter(correction => !correction.sentAt)

  static dispositionFileName = (disposition: Disposition): string =>
    fileNameFor('dyspozycja', disposition.id, 'xlsx')

  static invoiceFileName = (disposition: Disposition): string =>
    fileNameFor('faktura', disposition.id, 'pdf')

  static correctionFileName = (correction: DispositionCorrection): string =>
    fileNameFor('korekta', correction.id, 'xlsx')

  static downloadFile = (fileUrl: string | null, fileName: string): void => {
    if (!fileUrl) {
      return
    }

    void DownloadUtils.downloadFileFromUrl(fileUrl, fileName)
  }

  static upsertCorrection = (
    variables: UpsertCorrectionVariables,
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .upsertCorrection(variables)
      .then(res => {
        const payload = res.data?.upsertDispositionCorrection

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.corrections.saveError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.corrections.saveSuccess'))
        return true
      })
      .catch(() => false)

  static sendCorrection = (
    correctionId: DispositionCorrection['id'],
    addNotification: AddNotification,
    t: TFunction
  ): Promise<boolean> =>
    api.dispositions
      .sendCorrection(correctionId)
      .then(res => {
        const payload = res.data?.sendDispositionCorrection

        if (!payload?.success) {
          addNotification(
            NotifierType.ERROR,
            payload?.errors?.length
              ? payload.errors
              : t('agencies.dispositions.corrections.sendError')
          )
          return false
        }

        addNotification(NotifierType.SUCCESS, t('agencies.dispositions.corrections.sendSuccess'))
        return true
      })
      .catch(() => false)
}
